import { supabase } from '../config/supabase';
import type {
  AttendanceCertificateVerificationInput,
  GeneratedDocumentFileVerificationInput,
  PublicDocumentVerificationRecord,
} from '../utils/documentVerification';

export class DocumentVerificationService {
  static async getPublicDocumentVerification(
    token: string,
    tenantSlug?: string | null,
  ): Promise<PublicDocumentVerificationRecord | null> {
    const normalizedToken = String(token || '').trim();
    if (!normalizedToken) {
      return null;
    }

    const { data, error } = await supabase.rpc('get_public_document_verification', {
      p_token: normalizedToken,
      p_tenant_slug: tenantSlug?.trim() || null,
    });

    if (error) {
      console.error('Erro ao consultar verificação pública do documento:', error);
      throw error;
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return null;
    }

    return data as PublicDocumentVerificationRecord;
  }

  static async registerAttendanceCertificateVerification(
    input: AttendanceCertificateVerificationInput,
  ): Promise<PublicDocumentVerificationRecord | null> {
    const { data, error } = await supabase.rpc('register_attendance_certificate_document_verification', {
      p_client_id: input.clientId,
      p_tenant_slug: input.tenantSlug?.trim() || null,
      p_tenant_name: input.tenantName,
      p_document_title: input.documentTitle,
      p_document_id: null,
      p_patient_id: input.patientId || null,
      p_patient_name: input.patientName,
      p_patient_medical_record_number: input.patientMedicalRecordNumber || null,
      p_professional_name: input.professionalName,
      p_professional_role: input.professionalRole || null,
      p_professional_registration: input.professionalRegistration || null,
      p_signed_at: input.signedAt,
      p_status: 'valid',
      p_document_hash: input.documentHash,
      p_template_version: input.templateVersion,
      p_snapshot: input.snapshot || {},
      p_token: input.token,
    });

    if (error) {
      console.error('Erro ao registrar assinatura do atestado de presença:', error);
      throw error;
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return null;
    }

    return data as PublicDocumentVerificationRecord;
  }

  static async registerGeneratedDocumentFileVerification(
    input: GeneratedDocumentFileVerificationInput,
  ): Promise<PublicDocumentVerificationRecord | null> {
    const { data, error } = await supabase.rpc('register_generated_document_file_verification', {
      p_client_id: input.clientId,
      p_tenant_slug: input.tenantSlug?.trim() || null,
      p_tenant_name: input.tenantName?.trim() || null,
      p_document_type: input.documentType,
      p_document_id: input.documentId || null,
      p_document_title: input.documentTitle,
      p_patient_id: input.patientId || null,
      p_patient_name: input.patientName || null,
      p_patient_medical_record_number: input.patientMedicalRecordNumber || null,
      p_professional_name: input.professionalName || null,
      p_professional_role: input.professionalRole || null,
      p_professional_registration: input.professionalRegistration || null,
      p_signed_at: input.signedAt,
      p_status: input.status || 'valid',
      p_source_document_hash: input.documentHash,
      p_template_version: input.templateVersion,
      p_snapshot: input.snapshot || {},
      p_file_token: input.fileToken,
      p_document_token: input.sourceDocumentToken || null,
      p_file_hash: input.fileHash,
      p_generated_at: input.generatedAt,
      p_generated_by_user_id: input.generatedByUserId || null,
      p_generated_by_name: input.generatedByName || null,
      p_generation_channel: input.generationChannel,
      p_file_name: input.fileName || null,
      p_file_size_bytes: input.fileSizeBytes ?? null,
    });

    if (error) {
      console.error('Erro ao registrar arquivo PDF verificado:', error);
      throw error;
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return null;
    }

    return data as PublicDocumentVerificationRecord;
  }
}
